import React, { useState } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Button,
  VStack,
  Grid,
  HStack,
  Input,
  IconButton,
  Text,
  Box,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuList,
  RadioGroup,
  Radio,
  Stack,
} from "@chakra-ui/react";
import { ArrowBackIcon, CalendarIcon } from "@chakra-ui/icons";
import { FaMapMarkerAlt, FaTag } from "react-icons/fa";
import { CirclePicker, BlockPicker } from "react-color";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment-timezone";
import axios from "axios";
import Select from "react-select";
import currencyFormatter from "currency-formatter";
import Translate from "../../components/LanguageTranslation/Translate";
import { useTranslation } from "../../components/LanguageTranslation/TranslationContext";
import { textColor } from "./BudgetPlanner";
import axiosInstance from "../../helpers/axiosInstance";

const ExpenseModal = ({
  isOpen,
  onClose,
  addExpense,
  categories,
  newExpenseName,
  setNewExpenseName,
  newCost,
  isShared,
  setNewCost,
  selectedCategory,
  setSelectedCategory,
  selectedDate,
  setSelectedDate,
  selectedLocation,
  setSelectedLocation,
  selectedCurrency,
  setSelectedCurrency,
  selectedColor,
  setSelectedColor,
  setIsShared,
  isUpdatingExpense,
  modalHeader,
}) => {
  const { useTranslate } = useTranslation();
  const placeholderText = useTranslate("Currency");
  const placeholderexpense = useTranslate("Enter expense name...");
  const placeholderlocation = useTranslate("Search location...")
  const [isCategorySelection, setIsCategorySelection] = useState(false);
  const [isLocationSelection, setIsLocationSelection] = useState(false);
  const [locationOptions, setLocationOptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const openCategorySelection = () => setIsCategorySelection(true);
  const openLocationSelection = () => setIsLocationSelection(true);

  const handleBack = () => {
    if (isCategorySelection) {
      setIsCategorySelection(false);
    } else if (isLocationSelection) {
      setIsLocationSelection(false);
    }
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setIsCategorySelection(false);
  };

  const handleLocationSelect = (location) => {
    setSelectedLocation(location);
    setIsLocationSelection(false);
  };

  // Handle Location Search using OpenCage API
  // const handleLocationSearch = async (inputValue) => {
  //   if (!inputValue || inputValue.length < 3) return;

  //   setLoading(true);
  //   try {
  //     const response = await axios.get(
  //       `https://api.opencagedata.com/geocode/v1/json`,
  //       {
  //         params: {
  //           key: "66b78ee6f74941fd8645cf2c1dec446e", // Replace with your OpenCage API key
  //           q: inputValue,
  //           limit: 5,
  //         },
  //       }
  //     );

  //     const seen = new Set();
  //     const uniqueLocations = response.data.results
  //       .map((result) => ({
  //         label: result.formatted,
  //         value: result.geometry,
  //       }))
  //       .filter((location) => {
  //         if (seen.has(location.label)) return false;
  //         seen.add(location.label);
  //         return true;
  //       });

  //     setLocationOptions(uniqueLocations);
  //   } catch (error) {
  //     console.error("Error fetching locations:", error);
  //   } finally {
  //     setLoading(false);
  //   }
  // };

  const handleLocationSearch = async (inputValue) => {
    if (!inputValue || inputValue.length < 3) return;

    setLoading(true);
    try {
      // Call your Flask proxy endpoint instead of Google directly
      const response = await axiosInstance.get(
        "/budget_planner/places-autocomplete", // adjust prefix if needed
        {
          params: { input: inputValue },
        }
      );

      // The backend returns the full Google payload; extract predictions
      const predictions = response.data.predictions || [];

      // Map to your dropdown format
      const uniqueLocations = predictions.map((prediction) => ({
        label: prediction.description,
        value: prediction.place_id,
        // Optionally: you could store prediction.structured_formatting for more info
      }));

      // console.log("Received from backend proxy:", uniqueLocations);
      setLocationOptions(uniqueLocations);
    } catch (error) {
      console.error("Error fetching locations from backend:", error);
      if (error.response) {
        console.error("Backend error data:", error.response.data);
      }
    } finally {
      setLoading(false);
    }
  };

  // const handleDateChange = (date) => {
  //   // Convert the selected date to EST
  //   const estDate = moment(date)
  //     .tz("America/New_York")
  //     .format("YYYY-MM-DD HH:mm:ss");

  //   setSelectedDate(estDate); // Set the EST date in the state
  // };

  const handleDateChange = (date) => {
    // Store only the date part, ignoring the time
    const selectedDate = moment(date).format("YYYY-MM-DD");
    setSelectedDate(selectedDate);
  };

  // Get all currency codes from currency-formatter
  const currencyOptions = currencyFormatter.currencies.map((currency) => ({
    value: currency.code,
    label: `${currency.code} - ${currency.symbol || ""}`, // Display code and symbol (if available)
  }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size={{ base: "xs", md: "lg" }}
      isCentered
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent
        p={{ base: 3, md: 4 }}
        borderRadius="lg"
        maxH="90vh"
        scrollBehavior="inside"
      >
        <ModalHeader px={{ base: 2, md: 4 }}>
          {isCategorySelection || isLocationSelection ? (
            <HStack spacing={2}>
              <IconButton
                icon={<ArrowBackIcon />}
                onClick={handleBack}
                aria-label="Back"
                size="sm"
              />
              <Text fontSize={{ base: "md", md: "lg" }} mb={0}>
                <Translate
                  text={
                    isCategorySelection ? "Select category" : "Select location"
                  }
                />
              </Text>
            </HStack>
          ) : (
            <Text fontSize={{ base: "lg", md: "xl" }}>
              <Translate text={modalHeader} />
            </Text>
          )}
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody px={{ base: 2, md: 4 }} pt={2}>
          {!isCategorySelection && !isLocationSelection ? (
            <VStack spacing={4} align="stretch">
              {/* Category, Date, and Location Row */}
              <Stack
                direction={{ base: "column", md: "row" }}
                w="100%"
                spacing={2}
              >
                <Button
                  onClick={openCategorySelection}
                  variant="outline"
                  fontSize={{ base: "xs", md: "sm" }}
                  w="100%"
                >
                  <Translate text={selectedCategory || "Category"} />
                </Button>

                <Button
                  leftIcon={<CalendarIcon />}
                  variant="outline"
                  w="100%"
                  fontSize={{ base: "xs", md: "sm" }}
                >
                  <DatePicker
                    selected={selectedDate ? new Date(selectedDate) : null}
                    onChange={handleDateChange}
                    dateFormat="dd MMM yyyy"
                    customInput={
                      <Button
                        variant="unstyled"
                        w="100%"
                        whiteSpace="nowrap"
                        textOverflow="ellipsis"
                        fontSize={{ base: "xs", md: "sm" }}
                      >
                        <Translate
                          text={
                            selectedDate
                              ? moment(selectedDate).format("DD MMM YYYY")
                              : "Select date"
                          }
                        />
                      </Button>
                    }
                  />
                </Button>

                <Button
                  leftIcon={<FaMapMarkerAlt />}
                  onClick={openLocationSelection}
                  variant="outline"
                  w="100%"
                  fontSize={{ base: "xs", md: "sm" }}
                  noOfLines={1}
                >
                  <Translate
                    text={
                      selectedLocation ? selectedLocation.label : "Location"
                    }
                  />
                </Button>
              </Stack>

              {/* Currency and Cost */}
              <HStack spacing={2} w="full">
                {/* Currency Selector */}
                <Box flex="1" w={{ base: "55%", md: "30%", lg: "25%" }}>
                  <Select
                    options={currencyOptions}
                    value={selectedCurrency}
                    onChange={setSelectedCurrency}
                    placeholder={placeholderText}
                    isClearable={false}
                  />
                </Box>

                {/* Cost Input */}
                <Input
                  w={{ base: "45%", md: "70%", lg: "75%" }} // narrower on mobile
                  placeholder="0.00"
                  type="number"
                  value={newCost}
                  onChange={(e) => setNewCost(e.target.value)}
                  bg="gray.100"
                  borderRadius="md"
                  fontSize={{ base: "xs", md: "sm" }}
                />
              </HStack>

              {/* Color and Shared Type */}
              <Stack
                direction={{ base: "column", md: "row" }}
                spacing={4}
                align="stretch"
                w="full"
              >
                {/* Color Picker */}
                <Box w={{ base: "100%", md: "50%" }}>
                  <Menu>
                    <MenuButton
                      as={Button}
                      bg={selectedColor}
                      color={textColor(selectedColor)}
                      _hover={{ bg: selectedColor, opacity: 0.8 }}
                      _active={{ bg: selectedColor }}
                      _focus={{ bg: selectedColor, boxShadow: "outline" }}
                      w="full"
                    >
                      <Translate text="Expense Color" />
                    </MenuButton>
                    <MenuList p={2}>
                      <BlockPicker
                        color={selectedColor}
                        colors={[
                          "#FF5733",
                          "#FF8D33",
                          "#FFBD33",
                          "#FFD700",
                          "#DC143C",
                          "#FF3366",
                          "#FF33A1",
                          "#33FF57",
                          "#33FF8D",
                          "#33FFF0",
                          "#33A1FF",
                          "#3357FF",
                          "#5733FF",
                          "#8D33FF",
                          "#A133FF",
                          "#008000",
                          "#2E8B57",
                          "#4B0082",
                          "#808080",
                          "#A9A9A9",
                        ]}
                        onChange={(color) => setSelectedColor(color.hex)}
                      />
                    </MenuList>
                  </Menu>
                </Box>

                {/* Radio Group */}
                <Box w={{ base: "100%", md: "50%" }}>
                  <RadioGroup onChange={setIsShared} value={isShared}>
                    <Stack direction="row">
                      <Radio value="personal">
                        {" "}
                        <Translate text="Personal" />
                      </Radio>
                      <Radio value="shared">
                        <Translate text="Shared" />
                      </Radio>
                    </Stack>
                  </RadioGroup>
                </Box>
              </Stack>

              {/* Expense Name */}
              <InputGroup>
                <InputLeftElement pointerEvents="none">
                  <FaTag color="gray.300" />
                </InputLeftElement>
                <Input
                  placeholder={placeholderexpense} 
                  value={newExpenseName}
                  onChange={(e) => setNewExpenseName(e.target.value)}
                  bg="gray.100"
                  borderRadius="md"
                  fontSize={{ base: "sm", md: "md" }}
                />
              </InputGroup>
            </VStack>
          ) : isCategorySelection ? (
            <Grid
              templateColumns={{ base: "repeat(2, 1fr)", md: "repeat(3, 1fr)" }}
              gap={3}
            >
              {categories.map((category, index) => (
                <Button
                  key={index}
                  variant="outline"
                  onClick={() => handleCategorySelect(category.name)}
                  p={4}
                  fontSize={{ base: "sm", md: "md" }}
                >
                  <Translate text={category.name} />
                </Button>
              ))}
            </Grid>
          ) : (
            <VStack spacing={4} align="stretch">
              <Input
                placeholder={placeholderlocation} 
                onChange={(e) => handleLocationSearch(e.target.value)}
                bg="gray.100"
                borderRadius="md"
                fontSize={{ base: "sm", md: "md" }}
              />
              {loading ? (
                <Text>Loading...</Text>
              ) : locationOptions.length > 0 ? (
                <Box maxH="200px" overflowY="auto" w="full">
                  {locationOptions.map((location, index) => (
                    <Button
                      key={index}
                      variant="outline"
                      onClick={() => handleLocationSelect(location)}
                      w="full"
                      textAlign="left"
                      p={4}
                      whiteSpace="normal"
                    >
                      <Translate text={location.label} />
                    </Button>
                  ))}
                </Box>
              ) : (
                <Text>
                  <Translate text="No locations found" />
                </Text>
              )}
            </VStack>
          )}
        </ModalBody>

        {!isCategorySelection && !isLocationSelection && (
          <ModalFooter px={{ base: 2, md: 4 }}>
            <Button
              onClick={addExpense}
              w="full"
              bg="#286328"
              color="white"
              _hover={{ opacity: 0.9 }}
              isLoading={isUpdatingExpense}
              loadingText="Updating..."
              disabled={isUpdatingExpense}
            >
              <Translate text="Save" />
            </Button>
          </ModalFooter>
        )}
      </ModalContent>
    </Modal>
  );
};

export default ExpenseModal;
